import React from "react";
import { FileUpload } from "../UI/FileUpload";
import { Popover } from "flowbite-react";

interface CustomizationProps {
  formData: {
    primaryColor?: string;
    secondaryColor?: string;
    buttonColor?: string;
    qrCodeLogo?: File | string | null;
    fabButtonLogo?: File | string | null;
    faviconLogo?: File | string | null;
    supportedLanguages?: string[];
  };
  onUpdate: (field: string, value: any) => void;
  errors: { [key: string]: string };
}

export const Customization: React.FC<CustomizationProps> = ({
  formData,
  onUpdate,
  errors,
}) => {
  const handleColorChange = (
    colorType: "primary" | "secondary" | "button",
    value: string,
  ) => {
    onUpdate(`${colorType}Color`, value);
  };

  const handleFileSelect = (field: string, file: File | null) => {
    console.log(`File selection for ${field}:`, file);
    onUpdate(field, file);
  };

  return (
    <div className="form-step active">
      <div className="grid-3">
        <div className="form-field">
          <label htmlFor="primaryColor">Primary color</label>
          <div className="color-picker-group">
            <input
              type="color"
              id="primaryColorPicker"
              name="primaryColorPicker"
              value={formData.primaryColor || "#5855f8"}
              onChange={(e) => handleColorChange("primary", e.target.value)}
              aria-label="Primary color picker"
            />
            <input
              type="text"
              id="primaryColor"
              name="primaryColor"
              value={formData.primaryColor || "#5855f8"}
              onChange={(e) => handleColorChange("primary", e.target.value)}
              placeholder="Enter hex code"
            />
          </div>
        </div>
        <div className="form-field">
          <label htmlFor="secondaryColor">Secondary color</label>
          <div className="color-picker-group">
            <input
              type="color"
              id="secondaryColorPicker"
              name="secondaryColorPicker"
              value={formData.secondaryColor || "#ee4a8c"}
              onChange={(e) => handleColorChange("secondary", e.target.value)}
              aria-label="Secondary color picker"
            />
            <input
              type="text"
              id="secondaryColor"
              name="secondaryColor"
              value={formData.secondaryColor || "#ee4a8c"}
              onChange={(e) => handleColorChange("secondary", e.target.value)}
              placeholder="Enter hex code"
            />
          </div>
        </div>
        <div className="form-field">
          <label htmlFor="buttonColor">Button color</label>
          <div className="color-picker-group">
            <input
              type="color"
              id="buttonColorPicker"
              name="buttonColorPicker"
              value={formData.buttonColor || "#42BCF5"}
              onChange={(e) => handleColorChange("button", e.target.value)}
              aria-label="Button color picker"
            />
            <input
              type="text"
              id="buttonColor"
              name="buttonColor"
              value={formData.buttonColor || "#42BCF5"}
              onChange={(e) => handleColorChange("button", e.target.value)}
              placeholder="Enter hex code"
            />
          </div>
        </div>
      </div>

      <div className="grid-3 gapTop-4">
        <div className="form-field">
          <label htmlFor="qrCodeLogo" className="z-50">
            QR code logo (optional)
            <Popover
              trigger="hover"
              aria-label="QR code logo"
              placement="top"
              content={
                <div className="w-64 text-sm text-gray-500 dark:text-gray-400 z-50">
                  <div className="border-b border-gray-200 bg-gray-100 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 z-50">
                    <h3
                      id="default-popover"
                      className="font-semibold text-gray-900 dark:text-white"
                    >
                      QR Code Logo
                    </h3>
                  </div>
                  <div className="px-3 py-2">
                    <p>
                      This logo will be displayed in the center of QR codes
                      generated for your profile. It helps with branding and
                      makes your QR codes more recognizable.
                    </p>
                  </div>
                </div>
              }
            >
              <span className="inline-block">
                <svg
                  className="w-4 h-4 ms-2 text-gray-400 hover:text-gray-500"
                  aria-hidden="true"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
              </span>
            </Popover>
          </label>
          <FileUpload
            label=""
            onFileSelect={(file) => handleFileSelect("qrCodeLogo", file)}
            currentFile={formData.qrCodeLogo}
            accept={["image/svg+xml", "image/png", "image/jpeg", "image/jpg"]}
            maxSize={5}
            required={false}
            error={errors.qrCodeLogo}
            slice={5}
          />
        </div>

        <div className="form-field">
          <label htmlFor="fabButtonLogo">
            FAB button logo (optional)
            <Popover
              trigger="hover"
              aria-label="QR code logo"
              placement="top"
              content={
                <div className="w-64 text-sm text-gray-500 dark:text-gray-400 z-50">
                  <div className="border-b border-gray-200 bg-gray-100 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 z-50">
                    <h3
                      id="default-popover"
                      className="font-semibold text-gray-900 dark:text-white"
                    >
                      FAB button logo
                    </h3>
                  </div>
                  <div className="px-3 py-2">
                    <p>
                      Fab button is a button that is displayed in the bottom
                      right corner of the screen.
                    </p>
                  </div>
                </div>
              }
            >
              <span className="inline-block">
                <svg
                  className="w-4 h-4 ms-2 text-gray-400 hover:text-gray-500"
                  aria-hidden="true"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
              </span>
            </Popover>
          </label>
          <FileUpload
            label=""
            onFileSelect={(file) => handleFileSelect("fabButtonLogo", file)}
            currentFile={formData.fabButtonLogo}
            accept={["image/svg+xml", "image/png", "image/jpeg", "image/jpg"]}
            maxSize={5}
            required={false}
            error={errors.fabButtonLogo}
            slice={5}
          />
        </div>

        <div className="form-field">
          <label htmlFor="faviconLogo">
            Favicon logo (optional)
            <Popover
              trigger="hover"
              aria-label="QR code logo"
              placement="top"
              content={
                <div className="w-64 text-sm text-gray-500 dark:text-gray-400 z-50">
                  <div className="border-b border-gray-200 bg-gray-100 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 z-50">
                    <h3
                      id="default-popover"
                      className="font-semibold text-gray-900 dark:text-white"
                    >
                      Favicon logo
                    </h3>
                  </div>
                  <div className="px-3 py-2">
                    <p>
                      This logo will be displayed in the top left corner of the
                      browser tab. It helps with branding and makes your profile
                      more recognizable.
                    </p>
                  </div>
                </div>
              }
            >
              <span className="inline-block">
                <svg
                  className="w-4 h-4 ms-2 text-gray-400 hover:text-gray-500"
                  aria-hidden="true"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
              </span>
            </Popover>
          </label>
          <FileUpload
            label=""
            onFileSelect={(file) => handleFileSelect("faviconLogo", file)}
            currentFile={formData.faviconLogo}
            accept={["image/svg+xml", "image/png", "image/jpeg", "image/jpg"]}
            maxSize={5}
            required={false}
            error={errors.faviconLogo}
            slice={5}
          />
        </div>
      </div>

      <div className="form-field">
        <label htmlFor="supportedLanguage">
          Additional languages (optional)
          <Popover
            trigger="hover"
            aria-label="QR code logo"
            placement="top"
            content={
              <div className="w-64 text-sm text-gray-500 dark:text-gray-400 z-50">
                <div className="border-b border-gray-200 bg-gray-100 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 z-50">
                  <h3
                    id="default-popover"
                    className="font-semibold text-gray-900 dark:text-white"
                  >
                    Additional languages
                  </h3>
                </div>
                <div className="px-3 py-2">
                  <p>
                    This will allow you content to be translated to any other
                    language of your choice. The default language is English.
                  </p>
                </div>
              </div>
            }
          >
            <span className="inline-block">
              <svg
                className="w-4 h-4 ms-2 text-gray-400 hover:text-gray-500"
                aria-hidden="true"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                  clip-rule="evenodd"
                ></path>
              </svg>
            </span>
          </Popover>
        </label>
        <select
          id="supportedLanguage"
          name="supportedLanguage"
          value={formData.supportedLanguages?.[0] || ""}
          onChange={(e) => {
            const selected = e.target.value;
            onUpdate("supportedLanguages", selected ? [selected] : []);
          }}
        >
          <option value="">Select Additional language</option>
          <option value="es">Spanish (Español)</option>
          <option value="fr">French (Français)</option>
          <option value="de">German (Deutsch)</option>
          <option value="it">Italian (Italiano)</option>
          <option value="ja">Japanese (日本語)</option>
          <option value="zh">Chinese (中文)</option>
          <option value="da">Danish (Dansk)</option>
          <option value="sv">Swedish (Svenska)</option>
        </select>
      </div>
    </div>
  );
};
